import React, { useState, useEffect } from 'react';
import { X, Upload, Save } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ProjectUploadModal = ({ isOpen, onClose, onSubmit, projectToEdit }) => {
  const { isDarkMode } = useTheme();
  const isEditMode = !!projectToEdit;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Residential',
    tags: '',
    image: null,
    status: 'Draft'
  });
  const [isUploading, setIsUploading] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (projectToEdit) {
      setFormData({
        title: projectToEdit.title || '',
        description: projectToEdit.description || '',
        category: projectToEdit.category || 'Residential',
        tags: projectToEdit.tags ? projectToEdit.tags.join(', ') : '',
        image: null, // Keep image upload optional when editing
        status: projectToEdit.status || 'Draft'
      });
    }
  }, [projectToEdit]);

  const categories = [
    'Residential',
    'Commercial', 
    'Urban Planning',
    'Cultural',
    'Infrastructure',
    'Hospitality'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const projectData = {
        ...formData,
        id: isEditMode ? projectToEdit.id : Date.now(),
        author: isEditMode ? projectToEdit.author : 'Current User',
        date: isEditMode ? projectToEdit.date : new Date().toISOString().split('T')[0],
        likes: isEditMode ? projectToEdit.likes : 0,
        image: formData.image || (isEditMode ? projectToEdit.image : null),
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      onSubmit(projectData);
      onClose();
      
      // Reset form only if not editing
      if (!isEditMode) {
        setFormData({
          title: '',
          description: '',
          category: 'Residential',
          tags: '',
          image: null,
          status: 'Draft'
        });
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert(isEditMode ? 'Update failed. Please try again.' : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      } rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-b p-6 rounded-t-2xl`}>
          <div className="flex justify-between items-center">
            <h2 className={`text-2xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{isEditMode ? 'Edit Project' : 'Upload New Project'}</h2>
            <button 
              onClick={onClose}
              className={`${
                isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              } p-2 rounded-full transition-colors`}
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Project Title */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Project Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border border-gray-300 text-gray-900'
              }`}
              placeholder="Enter project title"
            />
          </div>

          {/* Project Description */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border border-gray-300 text-gray-900'
              }`}
              placeholder="Describe your project..."
            />
          </div>

          {/* Category and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Category *
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                list="category-suggestions"
                className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'border border-gray-300 text-gray-900'
                }`}
                placeholder="Enter category..."
              />
              <datalist id="category-suggestions">
                {categories.map(category => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'border border-gray-300 text-gray-900'
                }`}
              >
                <option value="Draft">Draft</option>
                <option value="Review">Review</option>
                <option value="Published">Published</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#AC5757] focus:border-transparent ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border border-gray-300 text-gray-900'
              }`}
              placeholder="Enter tags separated by commas (e.g., Modern, Sustainable, Community)"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Project Image {isEditMode ? '(Optional)' : '*'}
            </label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-[#AC5757] transition-colors ${
              isDarkMode ? 'border-gray-600' : 'border-gray-300'
            }`}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required={!isEditMode}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <Upload size={48} className={`mx-auto mb-4 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <p className={`mb-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {formData.image ? formData.image.name : 'Click to upload project image'}
                </p>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  PNG, JPG, JPEG up to 10MB
                </p>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isUploading}
              className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isEditMode ? 'Saving...' : 'Uploading...'}
                </>
              ) : (
                <>
                  {isEditMode ? <Save size={20} /> : <Upload size={20} />}
                  {isEditMode ? 'Save Changes' : 'Upload Project'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`px-6 py-3 border rounded-lg transition-colors ${
                isDarkMode
                  ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectUploadModal;
